// Token persistence.
//
// SECURITY TRADE-OFF (documented, not a shortcut): the backend returns the
// refresh token in the JSON body (not an httpOnly cookie), so to survive a
// page reload we persist the session in sessionStorage (cleared when the tab
// closes, narrower than localStorage). The access token is primarily held in
// memory. For production hardening the recommended change is to have the
// backend set the refresh token as an httpOnly, Secure, SameSite cookie and
// drop body persistence here — this module is the single place to change.

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  // Epoch ms at which the access token expires.
  expiresAt: number;
}

const KEY = 'finkit.session';
// While impersonating, the impersonator's own session is parked here so
// "Return to my account" can restore it without a fresh login.
const IMPERSONATOR_KEY = 'finkit.session.impersonator';

let memory: StoredSession | null = null;

export const tokenStorage = {
  load(): StoredSession | null {
    if (memory) return memory;
    try {
      const raw = sessionStorage.getItem(KEY);
      memory = raw ? (JSON.parse(raw) as StoredSession) : null;
    } catch {
      memory = null;
    }
    return memory;
  },

  save(session: StoredSession): void {
    memory = session;
    try {
      sessionStorage.setItem(KEY, JSON.stringify(session));
    } catch {
      /* sessionStorage unavailable (private mode) — memory still works for this tab */
    }
  },

  clear(): void {
    memory = null;
    try {
      sessionStorage.removeItem(KEY);
      // Also drop any parked impersonator session: a logout must fully end
      // impersonation so a later direct login never inherits a stale stash
      // (which would wrongly surface "Return to my account").
      sessionStorage.removeItem(IMPERSONATOR_KEY);
    } catch {
      /* ignore */
    }
  },

  accessToken(): string | null {
    return this.load()?.accessToken ?? null;
  },

  // Park the current session under the impersonator key before impersonating.
  stashImpersonator(session: StoredSession): void {
    try {
      sessionStorage.setItem(IMPERSONATOR_KEY, JSON.stringify(session));
    } catch {
      /* ignore */
    }
  },

  impersonatorStash(): StoredSession | null {
    try {
      const raw = sessionStorage.getItem(IMPERSONATOR_KEY);
      return raw ? (JSON.parse(raw) as StoredSession) : null;
    } catch {
      return null;
    }
  },

  clearImpersonatorStash(): void {
    try {
      sessionStorage.removeItem(IMPERSONATOR_KEY);
    } catch {
      /* ignore */
    }
  },
};
